import { DirectClientDetailsFormData, QuoteState, RecommendedPlan, RecommendedPlanBenefits, User, UserRole } from 'Application/models';
import { logout } from 'Application/service';

const tokenStorageKey: string = 'bearerToken';
const userDataStorageKey: string = 'userData';
const TIMEOUT_DURATION = 10 * 60 * 1000; // 10 minutes
let timeoutId: NodeJS.Timeout | null = null; // Store the timeout ID
type FilterInputs = {
    policyStatus: string;
    agency: string;
    country: string;
};

// Save bearer token to session storage
export const saveBearerTokenToStorage = (bearerToken: string) => {
    sessionStorage.setItem(tokenStorageKey, bearerToken);
    resetSessionTimeout(); // Reset the timeout when saving the token
};

export const getBearerTokenFromStorage = () => {
    const bearerToken = sessionStorage.getItem(tokenStorageKey);
    if (bearerToken === null) return null;
    return bearerToken;
};

export const deleteBearerTokenFromStorage = () => sessionStorage.removeItem(tokenStorageKey);

// Save user data to session storage
export const saveUserDataToStorage = (userData: User) => {
    sessionStorage.setItem(userDataStorageKey, JSON.stringify(userData));
    resetSessionTimeout(); // Reset the timeout when saving user data
};

export const getUserDataFromStorage = () => {
    const userData = sessionStorage.getItem(userDataStorageKey);
    if (userData === null) return null;
    return JSON.parse(userData) as User;
};
export const getUserRoleNameFromStorage = (): UserRole | null => {
    const userData = sessionStorage.getItem(userDataStorageKey);
    if (!userData) return null;

    const userObject = JSON.parse(userData) as User;
    return userObject.role || null;
};

export const getUserRegionFromStorage = (): string | null => {
    const userData = sessionStorage.getItem(userDataStorageKey);
    if (!userData) return null;
    const userObject = JSON.parse(userData) as User;
    return userObject.region || null;
};

export const deleteUserDataFromStorage = () => sessionStorage.removeItem(userDataStorageKey);

// Retrieve the agency name from session storage
export const getAgencyNameFromStorage = () => {
    const userData = sessionStorage.getItem(userDataStorageKey);
    if (userData === null) return null;

    const userObject = JSON.parse(userData) as User;
    return userObject.intermediaryName;
};

export const getAgencyIdFromStorage = () => {
    const userData = sessionStorage.getItem(userDataStorageKey);
    if (!userData) return null;

    const userObject = JSON.parse(userData) as User;
    return userObject.intermediaryId || null; // Return `null` if `agency` or `id` is missing
    // return userObject.intermediary?.id || null; // Return `null` if `agency` or `id` is missing
};

export const getAgencyBranchFromStorage = (): string | null => {
    const userData = sessionStorage.getItem(userDataStorageKey);
    if (userData === null) return null;

    const userObject = JSON.parse(userData) as User;
    return userObject.branch;
};

export const getAgencyLocalTravelStatus = (): boolean | null => {
    const userData = sessionStorage.getItem(userDataStorageKey);
    if (userData === null) return null;

    const userObject = JSON.parse(userData) as User;
    return userObject.intermediaryLocalTravelStatus;
};

export const retrieveEncryptedData = () => {
    const storedData = sessionStorage.getItem(userDataStorageKey);

    if (storedData === null) return null;

    try {
        const parsedData = JSON.parse(storedData);
        console.log(parsedData);

        // Check if role exists in the parsedData
        if (parsedData?.role) {
            return parsedData.role;
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error parsing stored data', error);
        return null;
    }
};

export const logoutUserAfterInactivity = () => {
    try {
        logout();
        deleteUserDataFromStorage();
        window.location.href = '/';
    } catch (error) {
        console.error(error);
    }
};

export const resetSessionTimeout = () => {
    if (timeoutId) {
        clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
        logoutUserAfterInactivity();
    }, TIMEOUT_DURATION);
};

export const monitorUserActivity = () => {
    const events = ['mousemove', 'keydown', 'scroll'];

    events.forEach((event) => {
        window.addEventListener(event, resetSessionTimeout);
    });

    resetSessionTimeout();
};

monitorUserActivity();

export const saveUserAuthEmail = (email: string): void => {
    sessionStorage.setItem('userAuthEmail', email);
};

export const getUserAuthEmail = (): string | null => {
    return sessionStorage.getItem('userAuthEmail');
};

export const deleteUserAuthEmail = (): void => {
    sessionStorage.removeItem('userAuthEmail');
};

// Retrieve the M-Pesa payment status from session storage
export const getMpesaPaymentStatusFromStorage = () => {
    const isMpesaPayment = sessionStorage.getItem('isMpesaPayment');

    if (isMpesaPayment === 'isMpesaTrue') {
        return true;
    } else if (isMpesaPayment === 'isMpesaFalse') {
        return false;
    } else {
        return null; // In case the key doesn't exist or has an unexpected value
    }
};

// Retrieve the M-Pesa Offline payment status from session storage
export const getMpesaOfflinePaymentStatusFromStorage = () => {
    const isMpesaOffline = sessionStorage.getItem('isMpesaOffline');

    if (isMpesaOffline === 'isMpesaOfflineTrue') {
        return true;
    } else if (isMpesaOffline === 'isMpesaOfflineTrue') {
        return false;
    } else {
        return null; // In case the key doesn't exist or has an unexpected value
    }
};

export const saveDirectClientQuotationDataToStorage = (
    quotationNumber: string,
    directClientDetails: DirectClientDetailsFormData,
    recommendedPlans: RecommendedPlan[],
    recommendedPlansBenefits: RecommendedPlanBenefits[],
    selectedPlan: RecommendedPlan,
    isCovidSelected: boolean,
    isWarAndTerrorismSelected: boolean
) => {
    const directClientQuotationData = {
        quotationNumber,
        directClientDetails,
        recommendedPlans,
        recommendedPlansBenefits,
        selectedPlan,
        isCovidSelected,
        isWarAndTerrorismSelected
    };

    const dataToStore = JSON.stringify(directClientQuotationData);
    //* Encrypt the stored data
    localStorage.setItem('directClientQuotationData', dataToStore);
};

export const getDirectClientQuotationDataFromStorage = (): Partial<QuoteState> | null => {
    const data = localStorage.getItem('directClientQuotationData');
    return data ? JSON.parse(data) : null;
};

export const deleteDirectClientQuotationDataFromStorage = () => localStorage.removeItem('directClientQuotationData');

export const savePendingQuotationDataToLocalStorage = (pendingQuotationData: Partial<QuoteState>) => {
    const dataToStore = JSON.stringify(pendingQuotationData);

    localStorage.setItem('pendingQuotationData', dataToStore);
};

export const savePendingQuotationStepToSessionStorage = (quotationStep: number) =>
    localStorage.setItem('pendingQuotationStep', quotationStep.toString());

export const getPendingQuotationStepFromSessionStorage = () =>
    localStorage.getItem('pendingQuotationStep') ? Number(localStorage.getItem('pendingQuotationStep')) : -1;

export const deletePendingQuotationStepFromSessionStorage = () => localStorage.removeItem('pendingQuotationStep');

export const getNavLoginStatusFromStorage = () => {
    const isNavLogin = sessionStorage.getItem('isNavLogin');

    if (isNavLogin === 'true') {
        return true;
    } else if (isNavLogin === 'false') {
        return false;
    } else {
        return null; // In case the key doesn't exist or has an unexpected value
    }
};

export const setReportsPageNumber = (pageNumber: number) => sessionStorage.setItem('reportsPage', pageNumber.toString());

export const getReportsPageNumber = () => {
    return Number(sessionStorage.getItem('reportsPage'));
};

export const setReportsSearchInput = (searchInput: string) => sessionStorage.setItem('reportsSearchInput', searchInput);

export const getReportsSearchInput = () => {
    return sessionStorage.getItem('reportsSearchInput');
};

export const removeReportsSearchInput = () => {
    const reportsSearchInput = sessionStorage.getItem('reportsSearchInput');
    if (!reportsSearchInput) return;

    sessionStorage.removeItem('reportsSearchInput');
};

export const setReportsFilterInputs = (filterInputs: FilterInputs) =>
    sessionStorage.setItem('reportsFilterInputs', JSON.stringify(filterInputs));

export const getReportsFilterInputs = () => {
    const reportsFilterInputs = sessionStorage.getItem('reportsFilterInputs');
    if (!reportsFilterInputs) return null;

    return JSON.parse(reportsFilterInputs) as FilterInputs;
};

export const removeFilterInputs = () => {
    const reportsFilterInputs = sessionStorage.getItem('reportsFilterInputs');
    if (!reportsFilterInputs) return;

    sessionStorage.removeItem('reportsFilterInputs');
};
